import { blinkConstants } from '@thoughtspot/blink-constants';
import { getPivotStatePropsFromDXPivotState } from '@thoughtspot/client-state-util';
import { formattingConstants, getFormatPattern, getFormattedValue, getLocale, } from '@thoughtspot/formatter-service';
import { create } from '@thoughtspot/logger';
import { getValueFormattedByConfig } from '@thoughtspot/number-format-util';
import { convertValueFromBackend, getCustomCalendarMapForColumn, getId, isAttribute, isColumnValidForNumberFormatting, isDateColumn, isEffectivelyNumeric, } from '@thoughtspot/visualization-column-util';
import _ from 'lodash';
import { getAxisConfig, getChartProperties, getChartSpecificProperties, getClientState, getColumn, getFormattingForColumn, getPivotState, getPivotSummaryMode, getQueriesFromChartModel, hasAttributesInPivotValues, isSorted, } from '../../core/chart-model-utils';
import { containsMeasureNamesOrMeasureValuesColumns, getSageColumnsFromColumnId, isMeasureNameColumn, isMeasureValueColumn, } from '../../core/measure-names-and-values';
import { generateQueryDefinitions, generateQueryFromAxisConfig, getDataArray, } from '../../data/data-util';
import { ChartEventTypes } from '../../index';
import { BaseTableDataModel } from './dx-table-data-model';
import { getSortOrder, sortMethod } from './dx-table-util';
import { createPivotStateFieldMap, DEFAULT_KEY_KEYWORD, getCalculatedSummaryValueForMeasureValueColumn, getCellValue, getDatasetKey, getKeyValueFromCellValue, getProcessedSummarySetForPivotQuery, getSortInfoFromAnswerColumn, getSortingFunctionForMeasureColumn, hasRowsOrColumnsExpandedManually, isSortingStateSaved, SortBy, } from './pivot-data-model-utils';
export const areaToAxisMap = {
    column: 'color',
    row: 'x',
    data: 'y',
};
export var SummaryModesEnum;
(function (SummaryModesEnum) {
    SummaryModesEnum["None"] = "None";
    SummaryModesEnum["percentOfRowTotal"] = "percentOfRowTotal";
    SummaryModesEnum["percentOfColumnTotal"] = "percentOfColumnTotal";
    SummaryModesEnum["percentOfRowGrandTotal"] = "percentOfRowGrandTotal";
    SummaryModesEnum["percentOfColumnGrandTotal"] = "percentOfColumnGrandTotal";
    SummaryModesEnum["percentOfGrandTotal"] = "percentOfGrandTotal";
})(SummaryModesEnum || (SummaryModesEnum = {}));
export const SummaryModeStringPath = {
    [SummaryModesEnum.None]: 'pivotTable.summaryMode.None',
    [SummaryModesEnum.percentOfRowTotal]: 'pivotTable.summaryMode.percentOfRowTotalv2',
    [SummaryModesEnum.percentOfColumnTotal]: 'pivotTable.summaryMode.percentOfColumnTotalv2',
    [SummaryModesEnum.percentOfRowGrandTotal]: 'pivotTable.summaryMode.percentOfRowGrandTotal',
    [SummaryModesEnum.percentOfColumnGrandTotal]: 'pivotTable.summaryMode.percentOfColumnGrandTotal',
    [SummaryModesEnum.percentOfGrandTotal]: 'pivotTable.summaryMode.percentOfGrandTotal',
};
const DEFAULT_PERCENT_PRECISION = {
    type: 'percent',
};
const logger = create('pivot-data-model');
export class PivotDataModel extends BaseTableDataModel {
    constructor(chartModel, dataArray, queryDefinitions, envOptions, renderOptions, getEventsMap) {
        super(chartModel, dataArray, queryDefinitions, envOptions, renderOptions, getEventsMap);
        this.chartModel = chartModel;
        this.queriesToFetch = [];
        this.columnDataFormatter = (column, value) => {
            if (_.isNil(value)) {
                return ' ';
            }
            if (isColumnValidForNumberFormatting(column)) {
                const formatPattern = getFormatPattern(column);
                const columnLocale = getLocale(column);
                return getValueFormattedByConfig(value, getFormattingForColumn(this.chartModel, getId(column)), { formatPattern, columnLocale });
            }
            const customCalendarMapForColum = this.getDisplayCustomCalendarMapForColumn(column);
            return getFormattedValue(column, value, true, {
                displayToCustomCalendarValueMap: customCalendarMapForColum,
                noShorten: true,
            });
        };
        this.measureColumnDataFormatter = (column, value) => {
            const formatPattern = getFormatPattern(column);
            const columnLocale = getLocale(column);
            if (_.isNil(value)) {
                return ' ';
            }
            if (!isEffectivelyNumeric(column)) {
                if (isDateColumn(column)) {
                    const customCalendarMapForColumn = this.getDisplayCustomCalendarMapForColumn(column);
                    if (_.isArray(value)) {
                        return value
                            .map(v => {
                            if (_.isNil(v)) {
                                return ' ';
                            }
                            const data = convertValueFromBackend(column, v);
                            return getFormattedValue(column, data, true, {
                                displayToCustomCalendarValueMap: customCalendarMapForColumn,
                                noShorten: true,
                            });
                        })
                            .toString()
                            .replace(/,/g, formattingConstants.COMPOSITE_COLUMN_VALUES_JOIN_KEY);
                    }
                    const data = convertValueFromBackend(column, value);
                    return getFormattedValue(column, data, true, {
                        displayToCustomCalendarValueMap: customCalendarMapForColumn,
                        noShorten: true,
                    });
                }
                if (isColumnValidForNumberFormatting(column)) {
                    if (_.isArray(value)) {
                        return value
                            .map(v => {
                            return getValueFormattedByConfig(v, getFormattingForColumn(this.chartModel, getId(column)), { formatPattern, columnLocale });
                        })
                            .toString()
                            .replace(/,/g, formattingConstants.COMPOSITE_COLUMN_VALUES_JOIN_KEY);
                    }
                    return getValueFormattedByConfig(value, getFormattingForColumn(this.chartModel, getId(column)), { formatPattern, columnLocale });
                }
                if (_.isArray(value)) {
                    return value
                        .toString()
                        .replace(/,/g, formattingConstants.COMPOSITE_COLUMN_VALUES_JOIN_KEY);
                }
                return value.toString();
            }
            if (_.isArray(value)) {
                if (isMeasureValueColumn(column)) {
                    return '';
                }
                return this.envOptions.getTranslatedText('pivotTable.UNDEFINED_RESULT');
            }
            if (typeof value === 'string') {
                if (isMeasureValueColumn(column)) {
                    const model = this.chartModel;
                    const measureValues = model.config.axisConfig[0].measureValues;
                    const { value: val, key } = getKeyValueFromCellValue(value);
                    if (_.isNil(val)) {
                        return ' ';
                    }
                    const idx = key % measureValues.length;
                    if (_.isNil(measureValues[idx])) {
                        return value;
                    }
                    const measureColumn = measureValues[idx];
                    const formatPattern = getFormatPattern(measureColumn);
                    const columnLocale = getLocale(measureColumn);
                    return getValueFormattedByConfig(val, getFormattingForColumn(model, getId(measureColumn)), { formatPattern, columnLocale });
                }
                return this.envOptions.getTranslatedText('pivotTable.UNDEFINED_RESULT');
            }
            return getValueFormattedByConfig(value, getFormattingForColumn(this.chartModel, getId(column)), { formatPattern, columnLocale });
        };
        this.getPivotRowsCount = () => {
            return this._pivotRowsCount;
        };
        this.setPivotRowsCount = (count) => {
            this._pivotRowsCount = count;
        };
        this.pivotSummaryMode = getPivotSummaryMode(chartModel);
        const { fetchPivotSummaryFromBackend, enableAttributesInPivotDataArea, } = envOptions.flags;
        this.enableAttributesInPivotDataArea =
            enableAttributesInPivotDataArea || false;
        this.fetchPivotSummaryFromBackend =
            fetchPivotSummaryFromBackend || false;
        if (this.fetchPivotSummaryFromBackend) {
            this.processSummaryData(chartModel, dataArray);
        }
        this.getEventsMap = getEventsMap;
        this._isDataSourceDefault = renderOptions.isDataSourceDefault;
        this.setClientStateUpdatedBySystemAction(true);
        this.setIsLoading(false);
    }
    get rows() {
        return this._rows;
    }
    get measures() {
        return this._measures;
    }
    getClientStateUpdatedBySystemAction() {
        return this._clientStateUpdatedBySystemAction;
    }
    setClientStateUpdatedBySystemAction(clientStateUpdated) {
        this._clientStateUpdatedBySystemAction = clientStateUpdated;
    }
    getIsLoading() {
        return this._isLoading;
    }
    setIsLoading(value) {
        this._isLoading = value;
    }
    get isDataSourceDefault() {
        return this._isDataSourceDefault;
    }
    getDisplayCustomCalendarMapForColumn(column) {
        return getCustomCalendarMapForColumn(column, this.displayToCustomCalendarValueMap);
    }
    setFetchDataApiCallMethod(fetchCall) {
        this.fetchDataForQuery = fetchCall;
    }
    getCustomCalendarMap() {
        return this.displayToCustomCalendarValueMap;
    }
    getPivotDataSource() {
        return {
            fields: this.getFields(),
            store: this._data.slice(),
            load: () => {
                var _a;
                const state = (_a = this.pivotInstance.current) === null || _a === void 0 ? void 0 : _a.instance.getDataSource().state();
                return this.loadDataSource(state);
            },
        };
    }
    loadDataSource(dxPivotState, updatedChartModel = null, isUserAction = true) {
        return new Promise((resolve, reject) => {
            if (this.queriesToFetch.length === 0 ||
                !this.fetchPivotSummaryFromBackend) {
                this.setIsLoading(false);
                resolve(this._data.slice());
                this.setClientStateUpdatedBySystemAction(true);
                return;
            }
            const state = getPivotStatePropsFromDXPivotState(dxPivotState);
            this.fetchAndUpdateNewQueryDataset(state, updatedChartModel, isUserAction)
                .then(() => {
                this.setIsLoading(false);
                this.queriesToFetch = [];
                resolve(this._data.slice());
                this.setClientStateUpdatedBySystemAction(true);
            })
                .catch(err => {
                reject();
            });
        });
    }
    min(di) {
        return this._min[di];
    }
    max(di) {
        return this._max[di];
    }
    unsetCustomizeText(dataSource) {
        this._measures.forEach(measureCol => {
            const fOpts = dataSource.field(getId(measureCol));
            if (isMeasureValueColumn(getColumn(this.chartModel, getId(measureCol)))) {
                fOpts.customizeText = ({ value }) => {
                    var _a;
                    return (_a = value.split(':')[1]) !== null && _a !== void 0 ? _a : value;
                };
            }
            else {
                delete fOpts.customizeText;
            }
            dataSource.field(getId(measureCol), fOpts);
        });
    }
    setCustomizeText(dataSource) {
        this._measures.forEach(measureCol => {
            const fOpts = dataSource.field(getId(measureCol));
            fOpts.customizeText = ({ value }) => this.measureColumnDataFormatter(measureCol, value);
            dataSource.field(getId(measureCol), fOpts);
        });
    }
    getFieldCaption(col) {
        return this._colIdToUniqueName[getId(col)];
    }
    getFields() {
        let sortInfo = null;
        const self = this;
        let sortField;
        let summarySortOrder;
        const pivotState = getPivotState(this.chartModel);
        const shouldSortUsingPreAppliedState = isSortingStateSaved(pivotState);
        const pivotStateFieldMap = createPivotStateFieldMap(pivotState);
        this._measures.forEach((measureCol) => {
            if (isSorted(this.chartModel, measureCol)) {
                sortField = getId(measureCol);
                summarySortOrder = getSortOrder(this.chartModel, measureCol);
            }
        });
        if (!shouldSortUsingPreAppliedState) {
            sortInfo = getSortInfoFromAnswerColumn(this._rows, this._columns, this._measures, this.chartModel);
        }
        const attributesInPivotValues = hasAttributesInPivotValues(this.chartModel);
        const isRowsOrColumnsExpandedManually = hasRowsOrColumnsExpandedManually(getPivotState(this.chartModel));
        const containsMeasureNames = containsMeasureNamesOrMeasureValuesColumns(getAxisConfig(this.chartModel));
        const columns = this._columns.map(col => {
            const columnId = getId(col);
            let fieldOverrides = {};
            const fieldById = pivotStateFieldMap.get(columnId);
            if (!_.isNil(fieldById) &&
                fieldById.area === blinkConstants.pivotTable.Areas.COLUMN) {
                fieldOverrides = fieldById;
            }
            if (isMeasureNameColumn(col)) {
                const measureColumns = getSageColumnsFromColumnId(this.chartModel, getId(this._measures[0]));
                const columnId = getId(col);
                fieldOverrides = {
                    ...fieldOverrides,
                    sortBySummaryField: columnId,
                    sortingMethod: getSortingFunctionForMeasureColumn(measureColumns),
                };
            }
            if (!_.isNil(sortInfo)) {
                if (sortInfo.measureSortColumnId) {
                    const fieldById1 = sortInfo.sortingMap.get(sortInfo.measureSortColumnId);
                    fieldOverrides = {
                        ...fieldOverrides,
                        ...fieldById1,
                    };
                    this.setClientStateUpdatedBySystemAction(true);
                }
                else if (sortInfo.sortingMap.get(columnId)) {
                    const fieldById1 = sortInfo.sortingMap.get(columnId);
                    fieldOverrides = {
                        ...fieldOverrides,
                        ...fieldById1,
                    };
                    this.setClientStateUpdatedBySystemAction(true);
                }
            }
            return {
                dataField: columnId,
                area: blinkConstants.pivotTable.Areas.COLUMN,
                isMeasure: isEffectivelyNumeric(col),
                caption: this.getFieldCaption(col),
                customizeText: ({ value }) => {
                    return this.columnDataFormatter(col, value);
                },
                sortBy: SortBy.VALUE,
                sortingMethod: sortMethod,
                sortBySummaryField: sortField,
                sortBySummaryPath: [],
                sortOrder: getSortOrder(this.chartModel, col) || summarySortOrder,
                expanded: (containsMeasureNames || attributesInPivotValues) &&
                    !isRowsOrColumnsExpandedManually,
                ...fieldOverrides,
            };
        });
        const rows = this._rows.map(col => {
            const columnId = getId(col);
            let fieldOverrides = {};
            const fieldById = pivotStateFieldMap.get(columnId);
            if (!_.isNil(fieldById) &&
                fieldById.area === blinkConstants.pivotTable.Areas.ROW) {
                fieldOverrides = fieldById;
            }
            if (isMeasureNameColumn(col)) {
                const measureColumns = getSageColumnsFromColumnId(this.chartModel, getId(this._measures[0]));
                const columnId = getId(col);
                fieldOverrides = {
                    ...fieldOverrides,
                    sortBySummaryField: columnId,
                    sortingMethod: getSortingFunctionForMeasureColumn(measureColumns),
                };
            }
            if (!_.isNil(sortInfo)) {
                if (sortInfo.measureSortColumnId) {
                    const fieldById1 = sortInfo.sortingMap.get(sortInfo.measureSortColumnId);
                    fieldOverrides = {
                        ...fieldOverrides,
                        ...fieldById1,
                    };
                    this.setClientStateUpdatedBySystemAction(true);
                }
                else if (sortInfo.sortingMap.get(columnId)) {
                    const fieldById1 = sortInfo.sortingMap.get(columnId);
                    fieldOverrides = {
                        ...fieldOverrides,
                        ...fieldById1,
                    };
                    this.setClientStateUpdatedBySystemAction(true);
                }
            }
            return {
                dataField: columnId,
                area: blinkConstants.pivotTable.Areas.ROW,
                isMeasure: isEffectivelyNumeric(col),
                caption: this.getFieldCaption(col),
                customizeText: ({ value }) => {
                    return this.columnDataFormatter(col, value);
                },
                sortBy: SortBy.VALUE,
                sortingMethod: sortMethod,
                sortBySummaryField: sortField,
                sortBySummaryPath: [],
                sortOrder: getSortOrder(this.chartModel, col) || summarySortOrder,
                expanded: (containsMeasureNames || attributesInPivotValues) &&
                    !isRowsOrColumnsExpandedManually,
                ...fieldOverrides,
            };
        });
        const measures = this._measures.map(col => {
            return {
                dataField: getId(col),
                area: blinkConstants.pivotTable.Areas.DATA,
                isMeasure: isEffectivelyNumeric(col),
                caption: this.getFieldCaption(col),
                summaryType: self.getEffectiveAggrType(col),
                calculateSummaryValue: summaryCell => {
                    const summaryValue = this.fetchPivotSummaryFromBackend
                        ? this.getSummaryCellValueFromModel(summaryCell)
                        : summaryCell.value();
                    if (this.pivotSummaryMode === SummaryModesEnum.None ||
                        isAttribute(col)) {
                        return summaryValue;
                    }
                    let grandTotalCell = null;
                    switch (this.pivotSummaryMode) {
                        case SummaryModesEnum.percentOfRowTotal:
                            grandTotalCell = summaryCell.parent('column');
                            break;
                        case SummaryModesEnum.percentOfColumnTotal:
                            grandTotalCell = summaryCell.parent('row');
                            break;
                        case SummaryModesEnum.percentOfRowGrandTotal:
                            grandTotalCell = summaryCell.grandTotal('column');
                            break;
                        case SummaryModesEnum.percentOfColumnGrandTotal:
                            grandTotalCell = summaryCell.grandTotal('row');
                            break;
                        case SummaryModesEnum.percentOfGrandTotal:
                            grandTotalCell = summaryCell.grandTotal();
                            break;
                        default:
                            break;
                    }
                    if (isMeasureValueColumn(col)) {
                        return getCalculatedSummaryValueForMeasureValueColumn(summaryCell, grandTotalCell, this.pivotSummaryMode, (cell) => this.getSummaryCellValueFromModel(cell), this.fetchPivotSummaryFromBackend);
                    }
                    if (_.isNil(grandTotalCell)) {
                        return 1;
                    }
                    if (grandTotalCell === summaryCell.grandTotal() &&
                        this.pivotSummaryMode !==
                            SummaryModesEnum.percentOfGrandTotal) {
                        return 1;
                    }
                    const grandTotalCellValue = this
                        .fetchPivotSummaryFromBackend
                        ? this.getSummaryCellValueFromModel(grandTotalCell)
                        : grandTotalCell.value();
                    const percentValue = summaryValue / grandTotalCellValue;
                    return _.isFinite(percentValue) ? percentValue : undefined;
                },
                summaryDisplayMode: isAttribute(col)
                    ? SummaryModesEnum.None
                    : self.pivotSummaryMode,
                dataType: isEffectivelyNumeric(col) ? 'number' : 'string',
                format: this.pivotSummaryMode === SummaryModesEnum.None
                    ? undefined
                    : DEFAULT_PERCENT_PRECISION,
                customizeText: ({ value }) => {
                    return this.measureColumnDataFormatter(col, value);
                },
            };
        });
        return []
            .concat(columns)
            .concat(rows)
            .concat(measures);
    }
    addQueriesToFetch(queries) {
        queries.forEach(query => {
            const chartQuery = generateQueryFromAxisConfig(query);
            let columnsInDataOrder = chartQuery.getColumnsInDataOrder();
            if (this.enableAttributesInPivotDataArea) {
                const yColumnIds = query.y.map(yColumn => yColumn.id);
                columnsInDataOrder = columnsInDataOrder.filter(answerColumn => {
                    return !_.includes(yColumnIds, answerColumn.id);
                });
            }
            const datasetKey = getDatasetKey(columnsInDataOrder);
            if (this._summaryData && !_.isNil(this._summaryData[datasetKey])) {
                return;
            }
            this.queriesToFetch.push(query);
        });
        return this.queriesToFetch;
    }
    parseAxisConfig(axisConfig) {
        this._min = {};
        this._max = {};
        this._rows = axisConfig[areaToAxisMap.row];
        this._columns = axisConfig[areaToAxisMap.column];
        this._measures = axisConfig[areaToAxisMap.data];
    }
    getParsedValue(rawData, colId) {
        const data = convertValueFromBackend(this.columnIdToColumn[colId], rawData);
        this.displayToCustomCalendarValueMap =
            this.displayToCustomCalendarValueMap || {};
        this.displayToCustomCalendarValueMap[colId] =
            this.displayToCustomCalendarValueMap[colId] || {};
        this.displayToCustomCalendarValueMap[colId][data] = rawData;
        return data;
    }
    getDataRowMeasureAndAttribute(rawData, colId, getDataRowAttribute, isColAttr) {
        let data = this.getParsedValue(rawData, colId);
        if (isColAttr) {
            data = getDataRowAttribute(data);
        }
        this.updateMinMax(data, colId);
        return data;
    }
    parseDefinitions(chartModel) {
        if (containsMeasureNamesOrMeasureValuesColumns(getAxisConfig(chartModel))) {
            this.parseAxisConfig(chartModel.config.axisConfig[0]);
        }
        else {
            this.parseAxisConfig(getAxisConfig(chartModel));
        }
        super.parseDefinitions(chartModel);
    }
    getSummaryCellValueFromModel(summaryCell) {
        const areaKey = summaryCell.field('data').dataField;
        const fieldColumns = [].concat(this._columns).concat(this._rows);
        let datasetKey = DEFAULT_KEY_KEYWORD;
        let summaryKey = DEFAULT_KEY_KEYWORD;
        fieldColumns.forEach((col) => {
            if (summaryCell.value(getId(col))) {
                summaryKey += `_${summaryCell.value(getId(col), true)}`;
                datasetKey += `_${getId(col)}`;
            }
        });
        const cellValue = _.get(this._summaryData, [
            datasetKey,
            'processedSummarySet',
            summaryKey,
            areaKey,
        ]);
        return getCellValue(getColumn(this.chartModel, areaKey), cellValue);
    }
    processSummaryData(chartModel, dataArray) {
        this._summaryData = {};
        const queryDefinitions = generateQueryDefinitions(chartModel);
        const queryDatasets = getDataArray(dataArray);
        this.updateSummaryData(queryDefinitions, queryDatasets);
    }
    updateSummaryData(queries, queryDatasets) {
        if (queries.length !== queryDatasets.length) {
            console.error('error: difference in the length of queries and data set');
            return;
        }
        let hasAttributes = false;
        queries.forEach((query, idx) => {
            if (idx === 0) {
                hasAttributes = query.yAxisColumns.some(yColumn => isAttribute(yColumn));
            }
            const dataset = queryDatasets[idx].getData();
            let columnsInDataOrder = query.getColumnsInDataOrder();
            if (this.enableAttributesInPivotDataArea) {
                const yColumnIds = query.yAxisColumns.map(yColumn => yColumn.id);
                columnsInDataOrder = columnsInDataOrder.filter(answerColumn => {
                    return !_.includes(yColumnIds, answerColumn.id);
                });
            }
            const datasetKey = getDatasetKey(columnsInDataOrder);
            const startTime = performance.now();
            const processedSummarySet = getProcessedSummarySetForPivotQuery(query, dataset, this.getDataRowAttribute, this._summaryData[datasetKey], hasAttributes);
            const endTime = performance.now();
            logger.log('getProcessedSummarySetForPivotQuery perf time', endTime - startTime, 'ms');
            this._summaryData[datasetKey] = {
                dataset,
                processedSummarySet,
            };
        });
    }
    async fetchAndUpdateNewQueryDataset(pivotState, updatedChartModel = null, isUserAction = true) {
        const chartModel = updatedChartModel || this.chartModel;
        const updatedAxisConfig = [
            ...getQueriesFromChartModel(chartModel),
            ...this.queriesToFetch,
        ];
        const eventsMap = this.getEventsMap();
        const updateAxisConfigAndClientState = eventsMap.get(ChartEventTypes.UpdateAxisConfigAndClientState);
        const updatedClientState = _.assign({}, getClientState(chartModel), {
            chartProperties: {
                ...getChartProperties(chartModel),
                chartSpecific: {
                    ...getChartSpecificProperties(chartModel),
                    pivotState,
                },
            },
        });
        const { data } = await updateAxisConfigAndClientState(updatedAxisConfig, updatedClientState, this.queriesToFetch, true, isUserAction);
        const queryDataArray = getDataArray(data);
        this.updateSummaryData(_.map(this.queriesToFetch, (axisConfig, idx) => generateQueryFromAxisConfig(axisConfig, idx)), queryDataArray);
    }
    updateMinMax(data, colId) {
        this._min[colId] =
            !this._min[colId] || data < this._min[colId]
                ? data
                : this._min[colId];
        this._max[colId] =
            !this._max[colId] || data > this._max[colId]
                ? data
                : this._max[colId];
    }
}
//# sourceMappingURL=pivot-data-model.js.map